import React, { useEffect, useRef } from 'react'
import ApexCharts, { ApexOptions } from 'apexcharts'
import { getCSSVariableValue } from '../../../../../_metronic/assets/ts/_utils'
import { useThemeMode } from '../../../../../_metronic/partials/layout/theme-mode/ThemeModeProvider'
import { GenerationAnalysis } from '../../../../services/generationAnalysisService'
import { MONTH_NAMES, formatMonthKey } from '../PeriodRangePicker'
import { formatKwh } from '../../utils'

type Props = {
  analysis: GenerationAnalysis
  height?: number
}

export const SERIES_COLORS = {
  generation: '--bs-primary',
  consumption: '--bs-warning',
  prognosis: '--bs-danger',
}

const formatCategory = (date: string, granularity: 'day' | 'month') => {
  if (granularity === 'month') {
    const [year, month] = date.split('-').map(Number)
    return `${MONTH_NAMES[month - 1]}/${String(year).substring(2)}`
  }
  const [, month, day] = date.split('-')
  return `${day}/${month}`
}

export function GenerationAnalysisChart({ analysis, height = 420 }: Props) {
  const chartRef = useRef<HTMLDivElement | null>(null)
  const { mode } = useThemeMode()

  useEffect(() => {
    if (!chartRef.current) return
    const chart = new ApexCharts(chartRef.current, getChartOptions(analysis, height))
    chart.render()
    return () => chart.destroy()
  }, [analysis, height, mode])

  return <div ref={chartRef} style={{ minHeight: height }} />
}

function getChartOptions(analysis: GenerationAnalysis, height: number): ApexOptions {
  const labelColor = getCSSVariableValue('--bs-gray-500')
  const borderColor = getCSSVariableValue('--bs-gray-200')
  const generationColor = getCSSVariableValue(SERIES_COLORS.generation)
  const consumptionColor = getCSSVariableValue(SERIES_COLORS.consumption)
  const prognosisColor = getCSSVariableValue(SERIES_COLORS.prognosis)

  const isDaily = analysis.granularity === 'day'
  const categories = analysis.points.map((point) => formatCategory(point.date, analysis.granularity))

  // No modo diário, marca o início de cada mês para facilitar a leitura de vários meses
  const monthAnnotations = isDaily && analysis.months.length > 1
    ? analysis.months.map((month) => ({
        x: formatCategory(`${month.month}-01`, 'day'),
        borderColor: labelColor,
        strokeDashArray: 3,
        label: {
          text: formatMonthKey(month.month),
          orientation: 'horizontal',
          borderColor: 'transparent',
          style: { background: 'transparent', color: labelColor, fontSize: '11px', fontWeight: 600 },
        },
      }))
    : []

  return {
    series: [
      { name: 'Geração', type: 'bar', data: analysis.points.map((point) => point.generation) },
      { name: isDaily ? 'Consumo (média diária)' : 'Consumo', type: 'area', data: analysis.points.map((point) => point.consumption) },
      { name: 'Prognóstico', type: 'line', data: analysis.points.map((point) => point.prognosis) },
    ],
    chart: {
      fontFamily: 'inherit',
      type: 'line',
      height,
      stacked: false,
      toolbar: { show: false },
      // toolbar: { show: true, tools: { download: true, zoom: true, zoomin: true, zoomout: true, pan: true, reset: true, selection: false } },
      zoom: { enabled: false },
      // zoom: { enabled: true, type: 'x', autoScaleYaxis: true },
      animations: { enabled: analysis.points.length < 400 },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: isDaily ? (analysis.points.length > 62 ? '85%' : '60%') : '45%',
        borderRadius: isDaily && analysis.points.length > 62 ? 2 : 5,
      },
    },
    legend: {
      show: true,
      position: 'top',
      horizontalAlign: 'right',
      fontWeight: 600,
      labels: { colors: labelColor },
      markers: { radius: 12 },
      itemMargin: { horizontal: 10 },
    },
    dataLabels: { enabled: false },
    stroke: {
      show: true,
      width: [0, 2, 3],
      curve: 'smooth',
      dashArray: [0, 0, 6],
    },
    fill: {
      type: ['solid', 'gradient', 'solid'],
      opacity: [0.9, 0.25, 1],
      gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.05, stops: [0, 95, 100] },
    },
    markers: { size: isDaily ? 0 : [0, 3, 4], strokeWidth: 0, hover: { size: 5 } },
    xaxis: {
      categories,
      tickAmount: isDaily ? Math.min(categories.length, 16) : undefined,
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: { rotate: -45, rotateAlways: isDaily && categories.length > 31, hideOverlappingLabels: true, style: { colors: labelColor, fontSize: '12px' } },
      tooltip: { enabled: false },
    },
    yaxis: {
      min: 0,
      forceNiceScale: true,
      title: { text: 'Energia (kWh)', style: { color: labelColor, fontWeight: 500 } },
      labels: {
        style: { colors: labelColor, fontSize: '12px' },
        formatter: (value) => (value >= 1000 ? `${(value / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mil` : `${Math.round(value)}`),
      },
    },
    annotations: { xaxis: monthAnnotations as any },
    states: {
      hover: { filter: { type: 'none', value: 0 } },
      active: { allowMultipleDataPointsSelection: false, filter: { type: 'none', value: 0 } },
    },
    tooltip: {
      shared: true,
      intersect: false,
      style: { fontSize: '12px' },
      x: {
        formatter: (_value, { dataPointIndex }) => {
          const date = analysis.points[dataPointIndex]?.date
          if (!date) return ''
          if (!isDaily) return formatMonthKey(date)
          const [year, month, day] = date.split('-')
          return `${day}/${month}/${year}`
        },
      },
      y: { formatter: (value) => (value === null || value === undefined ? 'Sem dado' : formatKwh(value)) },
    },
    colors: [generationColor, consumptionColor, prognosisColor],
    grid: { borderColor, strokeDashArray: 4, yaxis: { lines: { show: true } }, padding: { top: 10 } },
    noData: { text: 'Sem dados para o período selecionado', style: { color: labelColor } },
  }
}
